import { safeColor } from "../../../../engine/core/validation.js";
import { chevronIcon, closeIcon, trashIcon } from "../icons.js";
import { formatShortDateTime } from "../utils/dates.js";
import { escapeAttr, escapeHtml } from "../utils/html.js";
import { renderEmpty } from "./empty.js";
import { groupTags, tagLabel } from "../../../../engine/core/tag-filter.js";
import { ESTIMATE_UNITS, formatEstimate } from '../../../../engine/core/estimate.js';
import { LOCALES, getLocale, t } from '../../../../shared/i18n/index.js';
import { invitationMeta } from "./docs.js";
import { engine } from "../../../../engine/index.js";

const ESTIMATE_UNIT_KEYS = { days: "html.node.days", weeks: "html.node.weeks", months: "html.node.months" };
const THEME_KEYS = { light: "html.theme.light", dark: "html.theme.dark", nord: "mobile.theme.nord", sepia: "html.theme.sepia" };

export function renderSearchSheet(appState, deps) {
  const {
    uiState,
    getSearchResults,
    getSafeStatus,
  } = deps;
  if (!uiState.searchOpen) return "";
  const query = uiState.searchQuery.trim();
  const results = query ? getSearchResults(query, appState) : { nodes: [], notes: [] };

  return `
    <div class="mobile-panel-backdrop" data-mobile-action="close-search"></div>
    <section role="dialog" aria-modal="true" class="mobile-panel mobile-search-panel">
      <header class="mobile-panel-header">
        <button class="mobile-icon-btn" data-mobile-action="close-search" aria-label="${escapeAttr(t("common.close"))}">${closeIcon()}</button>
        <div class="mobile-panel-title">${escapeHtml(t("common.search"))}</div>
        <button class="mobile-text-btn" data-mobile-action="apply-search">${escapeHtml(t("common.search"))}</button>
      </header>
      <div class="mobile-panel-body">
        <input id="mobile-global-search"
               class="mobile-input-control"
               data-mobile-action="global-search"
               value="${escapeAttr(uiState.searchQuery)}"
               placeholder="${escapeAttr(t("mobile.search.placeholder"))}" />
        ${query ? `
          <div class="mobile-section-title">${escapeHtml(t("mobile.nodes"))}</div>
          <div class="mobile-search-results">
            ${results.nodes.length ? results.nodes.map((node) => renderNodeSearchResult(node, { getSafeStatus })).join("") : renderEmpty(t("mobile.outline.noMatch"))}
          </div>
          <div class="mobile-section-title">${escapeHtml(t("mobile.search.docNotes"))}</div>
          <div class="mobile-search-results">
            ${results.notes.length ? results.notes.map(renderNoteSearchResult).join("") : renderEmpty(t("mobile.notes.noMatch"))}
          </div>
        ` : `<div class="mobile-empty">${escapeHtml(t("mobile.search.enterTerm"))}</div>`}
      </div>
    </section>
  `;
}

function renderNodeSearchResult(node, { getSafeStatus }) {
  return `
    <button class="mobile-search-result" data-mobile-action="search-open-node" data-node-id="${escapeAttr(node.id)}">
      <span class="mobile-status-dot ${getSafeStatus(node)}" style="--node-color:${safeColor(node.color, "#6b7280")}"></span>
      <span>${escapeHtml(node.name || t("common.untitled"))}</span>
    </button>
  `;
}

function renderNoteSearchResult(note) {
  return `
    <button class="mobile-search-result" data-mobile-action="search-open-note" data-note-id="${escapeAttr(note.id)}">
      <span class="mobile-doc-dot"></span>
      <span>${escapeHtml(note.title || formatShortDateTime(note.created) || t("mobile.notes.untitled"))}</span>
    </button>
  `;
}

export function renderFormSheet(deps) {
  const {
    callbacks,
    uiState,
    findDoc,
    findFolder,
  } = deps;
  const form = uiState.form;
  if (!form) return "";
  // Prefer the user's in-flight typed value over the default placeholder so
  // a remote re-render mid-typing does not reset the current form input.
  const draftFields = (uiState.formDraft?.type === form.type)
    ? uiState.formDraft.fields
    : {};
  const fieldVal = (id, fallback) =>
    Object.prototype.hasOwnProperty.call(draftFields, id) ? draftFields[id] : fallback;
  if (form.type === "create-doc") {
    return `
      <div class="mobile-panel-backdrop" data-mobile-action="close-form"></div>
      <section role="dialog" aria-modal="true" class="mobile-panel mobile-form-panel">
        <header class="mobile-panel-header">
          <button class="mobile-icon-btn" data-mobile-action="close-form" aria-label="${escapeAttr(t("common.close"))}">${closeIcon()}</button>
          <div class="mobile-panel-title">${escapeHtml(t("mobile.form.newDocument"))}</div>
          <button class="mobile-text-btn" data-mobile-action="submit-create-doc">${escapeHtml(t("common.create"))}</button>
        </header>
        <div class="mobile-panel-body">
          <label class="mobile-field">
            <span>${escapeHtml(t("common.title"))}</span>
            <input id="mobile-create-doc-title" class="mobile-input-control" value="${escapeAttr(fieldVal("mobile-create-doc-title", t("mobile.form.newDocument")))}" />
          </label>
        </div>
      </section>
    `;
  }
  if (form.type === "create-folder") {
    return `
      <div class="mobile-panel-backdrop" data-mobile-action="close-form"></div>
      <section role="dialog" aria-modal="true" class="mobile-panel mobile-form-panel">
        <header class="mobile-panel-header">
          <button class="mobile-icon-btn" data-mobile-action="close-form" aria-label="${escapeAttr(t("common.close"))}">${closeIcon()}</button>
          <div class="mobile-panel-title">${escapeHtml(t("mobile.form.newFolder"))}</div>
          <button class="mobile-text-btn" data-mobile-action="submit-create-folder">${escapeHtml(t("common.create"))}</button>
        </header>
        <div class="mobile-panel-body">
          <label class="mobile-field">
            <span>${escapeHtml(t("common.name"))}</span>
            <input id="mobile-create-folder-name" class="mobile-input-control" value="${escapeAttr(fieldVal("mobile-create-folder-name", t("mobile.form.newFolder")))}" />
          </label>
        </div>
      </section>
    `;
  }
  if (form.type === "rename-doc") {
    const doc = findDoc(form.docId);
    return `
      <div class="mobile-panel-backdrop" data-mobile-action="close-form"></div>
      <section role="dialog" aria-modal="true" class="mobile-panel mobile-form-panel">
        <header class="mobile-panel-header">
          <button class="mobile-icon-btn" data-mobile-action="close-form" aria-label="${escapeAttr(t("common.close"))}">${closeIcon()}</button>
          <div class="mobile-panel-title">${escapeHtml(t("mobile.form.renameDocument"))}</div>
          <button class="mobile-text-btn" data-mobile-action="submit-rename-doc">${escapeHtml(t("common.save"))}</button>
        </header>
        <div class="mobile-panel-body">
          <label class="mobile-field">
            <span>${escapeHtml(t("common.title"))}</span>
            <input id="mobile-rename-doc-title" class="mobile-input-control" value="${escapeAttr(fieldVal("mobile-rename-doc-title", doc?.title || doc?.name || ""))}" />
          </label>
        </div>
      </section>
    `;
  }
  if (form.type === "rename-folder") {
    const folder = findFolder(form.folderId);
    return `
      <div class="mobile-panel-backdrop" data-mobile-action="close-form"></div>
      <section role="dialog" aria-modal="true" class="mobile-panel mobile-form-panel">
        <header class="mobile-panel-header">
          <button class="mobile-icon-btn" data-mobile-action="close-form" aria-label="${escapeAttr(t("common.close"))}">${closeIcon()}</button>
          <div class="mobile-panel-title">${escapeHtml(t("mobile.form.renameFolder"))}</div>
          <button class="mobile-text-btn" data-mobile-action="submit-rename-folder">${escapeHtml(t("common.save"))}</button>
        </header>
        <div class="mobile-panel-body">
          <label class="mobile-field">
            <span>${escapeHtml(t("common.name"))}</span>
            <input id="mobile-rename-folder-name" class="mobile-input-control" value="${escapeAttr(fieldVal("mobile-rename-folder-name", folder?.name || ""))}" />
          </label>
        </div>
      </section>
    `;
  }
  if (form.type === "rename-node") {
    const node = callbacks.Store.find(form.nodeId);
    return `
      <div class="mobile-panel-backdrop" data-mobile-action="close-form"></div>
      <section role="dialog" aria-modal="true" class="mobile-panel mobile-form-panel">
        <header class="mobile-panel-header">
          <button class="mobile-icon-btn" data-mobile-action="close-form" aria-label="${escapeAttr(t("common.close"))}">${closeIcon()}</button>
          <div class="mobile-panel-title">${escapeHtml(t("mobile.form.renameNode"))}</div>
          <button class="mobile-text-btn" data-mobile-action="submit-rename-node">${escapeHtml(t("common.save"))}</button>
        </header>
        <div class="mobile-panel-body">
          <label class="mobile-field">
            <span>${escapeHtml(t("common.name"))}</span>
            <input id="mobile-rename-node-name" class="mobile-input-control" value="${escapeAttr(fieldVal("mobile-rename-node-name", node?.name || ""))}" />
          </label>
        </div>
      </section>
    `;
  }
  if (form.type === "add-node") {
    const parent = callbacks.Store.find(form.parentId);
    const definitions = deps.getTagDefinitions(form.parentId);
    const estimate = form.estimate;
    const spec = ESTIMATE_UNITS[estimate?.unit];
    return `
      <div class="mobile-panel-backdrop" data-mobile-action="close-form"></div>
      <section role="dialog" aria-modal="true" class="mobile-panel mobile-form-panel">
        <header class="mobile-panel-header">
          <button class="mobile-icon-btn" data-mobile-action="close-form" aria-label="${escapeAttr(t("common.close"))}">${closeIcon()}</button>
          <div class="mobile-panel-title">${escapeHtml(t("mobile.form.addNode"))}</div>
          <button class="mobile-text-btn" data-mobile-action="submit-add-node">${escapeHtml(t("common.add"))}</button>
        </header>
        <div class="mobile-panel-body">
          <div class="mobile-muted">${escapeHtml(t("mobile.form.parent", { name: parent?.name || t("mobile.move.currentFocus") }))}</div>
          <label class="mobile-field">
            <span>${escapeHtml(t("common.name"))}</span>
            <input id="mobile-add-node-name" class="mobile-input-control" enterkeyhint="done" value="${escapeAttr(fieldVal("mobile-add-node-name", t("mobile.form.newNode")))}" />
          </label>
          <div class="mobile-field">
            <span>${escapeHtml(t("common.tags"))}</span>
            <div class="mobile-creation-tags" role="group" aria-label="${escapeAttr(t("mobile.form.newNodeTags"))}">
              ${definitions.map(tag => `<button type="button" class="modal-tag-pill ${form.tagIds.includes(tag.id) ? 'active' : ''}"
                data-mobile-action="add-node-tag" data-tag-id="${escapeAttr(tag.id)}"
                aria-pressed="${form.tagIds.includes(tag.id)}">${escapeHtml(tagLabel(tag))}</button>`).join('')}
            </div>
          </div>
          <div class="mobile-field mobile-creation-estimate">
            <span>${escapeHtml(t("html.node.estimate"))}</span>
            <div class="estimate-units" role="group" aria-label="${escapeAttr(t("html.node.estimateUnit"))}">
              ${[['', t("common.none")], ...Object.entries(ESTIMATE_UNITS).map(([unit, info]) => [unit, ESTIMATE_UNIT_KEYS[unit] ? t(ESTIMATE_UNIT_KEYS[unit]) : info.label])].map(([unit, label]) => `
                <button type="button" class="modal-tag-pill ${(estimate?.unit || '') === unit ? 'active' : ''}"
                  data-mobile-action="add-node-estimate-unit" data-unit="${unit}"
                  aria-pressed="${(estimate?.unit || '') === unit}">${escapeHtml(label)}</button>`).join('')}
            </div>
            ${estimate ? `<div class="mobile-estimate-slider">
              <input type="range" id="mobile-add-node-estimate" min="1" max="${spec.max}" step="1" value="${estimate.value}"
                aria-label="${escapeAttr(t("html.node.estimate"))}" aria-valuetext="${formatEstimate(estimate)}" list="mobile-estimate-ticks" />
              <output for="mobile-add-node-estimate">${formatEstimate(estimate)}</output>
              <datalist id="mobile-estimate-ticks">${Array.from({length: spec.max}, (_, i) => `<option value="${i + 1}"></option>`).join('')}</datalist>
            </div>` : ''}
          </div>
        </div>
      </section>
    `;
  }
  return "";
}

export function renderConfirmSheet({ uiState }) {
  const confirm = uiState.confirm;
  if (!confirm) return "";
  const title = confirm.title || t("common.confirm");
  const message = confirm.message || t("mobile.confirm.sure");
  // Confirm-button label and styling depend on the action's nature.
  // Recovery and logout are not destructive; delete is.
  let actionLabel = t("common.ok");
  let actionClass = "mobile-secondary-btn";
  if (confirm.type === "delete-note" || confirm.type === "delete-node" ||
      confirm.type === "delete-doc" || confirm.type === "delete-folder") {
    actionLabel = t("common.delete");
    actionClass = "mobile-secondary-btn danger";
  } else if (confirm.type === "discard-note-edit" || confirm.type === "discard-node-edit") {
    actionLabel = t("mobile.confirm.discard");
    actionClass = "mobile-secondary-btn danger";
  } else if (confirm.type === "recover-note" || confirm.type === "recover-node") {
    actionLabel = t("mobile.restore");
    actionClass = "mobile-primary-btn";
  } else if (confirm.type === "logout") {
    actionLabel = t("common.logout");
    actionClass = "mobile-secondary-btn danger";
  } else if (confirm.type === "logout-everywhere") {
    actionLabel = t("account.logoutAll.confirm");
    actionClass = "mobile-secondary-btn danger";
  } else if (confirm.type === "decline-invitation") {
    actionLabel = t("share.inbox.decline");
    actionClass = "mobile-secondary-btn danger";
  } else if (confirm.type === "ignore-inviter") {
    actionLabel = t("share.inbox.ignore");
    actionClass = "mobile-secondary-btn danger";
  } else if (confirm.type === "unlink-node") {
    // The linked map stays in the vault: only the link goes.
    actionLabel = t("app.unlink.confirm");
    actionClass = "mobile-secondary-btn danger";
  } else if (confirm.type === "request-link-access") {
    actionLabel = t("app.requestAccess.title");
    actionClass = "mobile-primary-btn";
  } else if (confirm.type === "regenerate-recovery") {
    // Not destructive to data — it re-wraps the same master key — but it does
    // invalidate the previous phrase, so it is not a plain "OK" either.
    actionLabel = t("account.phrase.generate");
    actionClass = "mobile-primary-btn";
  }
  return `
    <div class="mobile-panel-backdrop" data-mobile-action="close-confirm"></div>
    <section role="dialog" aria-modal="true" class="mobile-panel mobile-confirm-panel">
      <header class="mobile-panel-header">
        <button class="mobile-icon-btn" data-mobile-action="close-confirm" aria-label="${escapeAttr(t("common.close"))}">${closeIcon()}</button>
        <div class="mobile-panel-title">${escapeHtml(title)}</div>
      </header>
      <div class="mobile-panel-body">
        <p class="mobile-confirm-message">${escapeHtml(message)}</p>
        <div class="mobile-note-actions">
          <button class="mobile-secondary-btn" data-mobile-action="close-confirm">${escapeHtml(t("common.cancel"))}</button>
          <button class="${actionClass}" data-mobile-action="confirm-action">${escapeHtml(actionLabel)}</button>
        </div>
      </div>
    </section>
  `;
}

export function renderMoveSheet(appState, deps) {
  const {
    callbacks,
    uiState,
    collectTreeEntries,
    getMoveSuggestions,
    isDescendantOf,
  } = deps;
  if (!uiState.moveNodeId) return "";
  const moving = callbacks.Store.find(uiState.moveNodeId);
  if (!moving) return "";
  const targets = collectTreeEntries(appState.data)
    .filter(({ node }) => node.id !== moving.id && !isDescendantOf(moving.id, node.id));
  const suggestions = getMoveSuggestions(moving, appState);

  return `
    <div class="mobile-panel-backdrop" data-mobile-action="close-move-node"></div>
    <section role="dialog" aria-modal="true" class="mobile-panel mobile-move-panel">
      <header class="mobile-panel-header">
        <button class="mobile-icon-btn" data-mobile-action="close-move-node" aria-label="${escapeAttr(t("common.close"))}">${closeIcon()}</button>
        <div class="mobile-panel-title">${escapeHtml(t("mobile.move.title", { name: moving.name || t("mobile.node.heading") }))}</div>
      </header>
      <div class="mobile-panel-body">
        <div class="mobile-muted">${escapeHtml(t("mobile.move.hint"))}</div>
        ${suggestions.length ? `
          <div class="mobile-section-title">${escapeHtml(t("mobile.move.suggested"))}</div>
          <div class="mobile-move-list suggested">
            ${suggestions.map(({ node, label }) => `
              <button class="mobile-move-target suggested"
                      data-mobile-action="move-node-to-parent"
                      data-parent-id="${escapeAttr(node.id)}">
                <span>${escapeHtml(label)}</span>
                <small>${escapeHtml(node.name || t("common.untitled"))}</small>
              </button>
            `).join("")}
          </div>
        ` : ""}
        <div class="mobile-section-title">${escapeHtml(t("mobile.move.allNodes"))}</div>
        <div class="mobile-move-list">
          ${targets.map(({ node, depth }) => `
            <button class="mobile-move-target"
                    style="--depth:${depth}"
                    data-mobile-action="move-node-to-parent"
                    data-parent-id="${escapeAttr(node.id)}">
              <span>${escapeHtml(node.name || t("common.untitled"))}</span>
            </button>
          `).join("")}
        </div>
      </div>
    </section>
  `;
}

export function renderDocActionsSheet(appState, deps) {
  const {
    uiState,
    findDoc,
    isOwnerDoc,
  } = deps;
  if (!uiState.docActionsId) return "";
  const doc = findDoc(uiState.docActionsId);
  if (!doc) return "";
  const title = doc.title || doc.name || t("common.untitled");
  const active = doc.docId === appState.currentDocId;
  const canWrite = isOwnerDoc(doc);

  return `
    <div class="mobile-panel-backdrop" data-mobile-action="close-doc-actions"></div>
    <section role="dialog" aria-modal="true" class="mobile-panel mobile-actions-panel">
      <header class="mobile-panel-header">
        <button class="mobile-icon-btn" data-mobile-action="close-doc-actions" aria-label="${escapeAttr(t("common.close"))}">${closeIcon()}</button>
        <div class="mobile-panel-title">${escapeHtml(title)}</div>
      </header>
      <div class="mobile-panel-body mobile-action-list">
        <button class="mobile-action-row" data-mobile-action="doc-action-open" data-doc-id="${escapeAttr(doc.docId)}">
          <span>${escapeHtml(t("common.open"))}</span>
          ${active ? `<small>${escapeHtml(t("mobile.current"))}</small>` : ""}
        </button>
        ${canWrite ? `
          <button class="mobile-action-row" data-mobile-action="doc-action-rename" data-doc-id="${escapeAttr(doc.docId)}">
            <span>${escapeHtml(t("common.rename"))}</span>
          </button>
          <button class="mobile-action-row" data-mobile-action="doc-action-move" data-doc-id="${escapeAttr(doc.docId)}">
            <span>${escapeHtml(t("mobile.docActions.moveToFolder"))}</span>
          </button>
          <button class="mobile-action-row" data-mobile-action="doc-action-duplicate" data-doc-id="${escapeAttr(doc.docId)}"><span>${escapeHtml(t("mobile.docActions.duplicate"))}</span></button>
          <button class="mobile-action-row" data-mobile-action="doc-action-export" data-doc-id="${escapeAttr(doc.docId)}"><span>${escapeHtml(t("mobile.docActions.export"))}</span></button>
          <button class="mobile-action-row danger" data-mobile-action="doc-action-delete" data-doc-id="${escapeAttr(doc.docId)}">
            <span>${escapeHtml(t("common.delete"))}</span>
            ${trashIcon()}
          </button>
        ` : `<div class="mobile-muted">${escapeHtml(t("mobile.docActions.sharedHint"))}</div>`}
      </div>
    </section>
  `;
}

export function renderFolderActionsSheet({ uiState, findFolder }) {
  if (!uiState.folderActionsId) return "";
  const folder = findFolder(uiState.folderActionsId);
  if (!folder) return "";
  const isRoot = folder.parentId === null;

  return `
    <div class="mobile-panel-backdrop" data-mobile-action="close-folder-actions"></div>
    <section role="dialog" aria-modal="true" class="mobile-panel mobile-actions-panel">
      <header class="mobile-panel-header">
        <button class="mobile-icon-btn" data-mobile-action="close-folder-actions" aria-label="${escapeAttr(t("common.close"))}">${closeIcon()}</button>
        <div class="mobile-panel-title">${escapeHtml(folder.name || t("common.folder"))}</div>
      </header>
      <div class="mobile-panel-body mobile-action-list">
        <button class="mobile-action-row" data-mobile-action="folder-action-open" data-folder-id="${escapeAttr(folder.id)}">
          <span>${escapeHtml(t("common.open"))}</span>
        </button>
        <button class="mobile-action-row" data-mobile-action="folder-action-new" data-folder-id="${escapeAttr(folder.id)}">
          <span>${escapeHtml(t("mobile.folderActions.newSubfolder"))}</span>
        </button>
        <button class="mobile-action-row" data-mobile-action="folder-action-rename" data-folder-id="${escapeAttr(folder.id)}">
          <span>${escapeHtml(t("common.rename"))}</span>
        </button>
        ${isRoot ? "" : `
          <button class="mobile-action-row" data-mobile-action="folder-action-move" data-folder-id="${escapeAttr(folder.id)}"><span>${escapeHtml(t("vault.folder.move"))}</span></button>
          <button class="mobile-action-row danger" data-mobile-action="folder-action-delete" data-folder-id="${escapeAttr(folder.id)}">
            <span>${escapeHtml(t("common.delete"))}</span>
            ${trashIcon()}
          </button>
        `}
      </div>
    </section>
  `;
}

export function renderMoveDocSheet(deps) {
  const {
    callbacks,
    uiState,
    collectFolderEntries,
    findDoc,
    getRootFolderId,
    normalizeFolderId,
  } = deps;
  if (!uiState.moveDocId) return "";
  const doc = findDoc(uiState.moveDocId);
  const index = callbacks.VaultStorage?.getIndex?.();
  if (!doc || !index) return "";
  const rootFolderId = getRootFolderId(index);
  const folders = collectFolderEntries(index);

  return `
    <div class="mobile-panel-backdrop" data-mobile-action="close-move-doc"></div>
    <section role="dialog" aria-modal="true" class="mobile-panel mobile-move-panel">
      <header class="mobile-panel-header">
        <button class="mobile-icon-btn" data-mobile-action="close-move-doc" aria-label="${escapeAttr(t("common.close"))}">${closeIcon()}</button>
        <div class="mobile-panel-title">${escapeHtml(t("mobile.move.title", { name: doc.title || doc.name || t("mobile.docProps.document") }))}</div>
      </header>
      <div class="mobile-panel-body">
        <div class="mobile-muted">${escapeHtml(t("mobile.move.folderHint"))}</div>
        <div class="mobile-move-list">
          ${folders.map(({ folder, depth }) => {
            const active = normalizeFolderId(doc.folderId, rootFolderId) === folder.id;
            return `
              <button class="mobile-move-target ${active ? "active" : ""}"
                      style="--depth:${depth}"
                      data-mobile-action="move-doc-to-folder"
                      data-folder-id="${escapeAttr(folder.id)}">
                <span>${escapeHtml(folder.name || t("common.folder"))}</span>
                ${active ? `<small>${escapeHtml(t("mobile.current"))}</small>` : ""}
              </button>
            `;
          }).join("")}
        </div>
      </div>
    </section>
  `;
}

export function renderStatusSheet(deps) {
  const {
    callbacks,
    uiState,
    getBoardColumns,
    getNodeTagIds,
    getTagDefinitions,
  } = deps;
  if (!uiState.statusNodeId) return "";
  const node = callbacks.Store.find(uiState.statusNodeId);
  if (!node) return "";
  const tagDefs = getTagDefinitions();
  const tagMap = new Map(tagDefs.map((tag) => [tag.id, tag]));
  const columns = getBoardColumns(tagDefs);
  const currentIds = getNodeTagIds(node);

  return `
    <div class="mobile-panel-backdrop" data-mobile-action="close-status"></div>
    <section role="dialog" aria-modal="true" class="mobile-panel mobile-actions-panel">
      <header class="mobile-panel-header">
        <button class="mobile-icon-btn" data-mobile-action="close-status" aria-label="${escapeAttr(t("common.close"))}">${closeIcon()}</button>
        <div class="mobile-panel-title">${escapeHtml(t("mobile.status.title"))}</div>
      </header>
      <div class="mobile-panel-body mobile-action-list">
        ${columns.map((col) => {
          const tag = tagMap.get(col.tagId);
          const active = currentIds.includes(col.tagId);
          return `
            <button class="mobile-action-row ${active ? "active" : ""}"
                    data-mobile-action="set-card-status"
                    data-node-id="${escapeAttr(node.id)}"
                    data-tag-id="${escapeAttr(col.tagId)}"
                    data-source-tag-id="${escapeAttr(uiState.statusSourceTagId || "")}">
              <span>${escapeHtml(tagLabel(tag) || t("mobile.board.column"))}</span>
              ${active ? `<small>${escapeHtml(t("mobile.current"))}</small>` : ""}
            </button>
          `;
        }).join("") || renderEmpty(t("mobile.status.none"))}
      </div>
    </section>
  `;
}

export function renderFilterSheet(appState, deps) {
  const {
    uiState,
    getTagDefinitions,
    getTagCategories,
  } = deps;
  if (!uiState.filterOpen) return "";
  const tagDefs = getTagDefinitions();
  const highlighted = appState.highlightedTagIds || [];
  const hidden = appState.hiddenTagIds || [];

  return `
    <div class="mobile-panel-backdrop" data-mobile-action="close-filter"></div>
    <section role="dialog" aria-modal="true" class="mobile-panel mobile-filter-panel">
      <header class="mobile-panel-header">
        <button class="mobile-icon-btn" data-mobile-action="close-filter" aria-label="${escapeAttr(t("common.close"))}">${closeIcon()}</button>
        <div class="mobile-panel-title">${escapeHtml(t("common.tags"))}</div>
        <button class="mobile-text-btn" data-mobile-action="clear-filters">${escapeHtml(t("common.clear"))}</button>
      </header>
      <div class="mobile-panel-body">
        ${groupTags(tagDefs, getTagCategories?.() || []).map((group) => `
          <div class="mobile-filter-group-title">${escapeHtml(group.name)}</div>
          ${[...group.tags.map((tag) => ({ id: tag.id, label: escapeHtml(tagLabel(tag)), synthetic: false })),
             { id: group.notTaggedId, label: `<em>${escapeHtml(t("app.sidebarTags.notTagged"))}</em>`, synthetic: true }].map((entry) => {
            // "Show" is active while the entry is NOT in hiddenTagIds — a
            // single visibility toggle, like the desktop eye.
            const isHighlighted = highlighted.includes(entry.id);
            const isVisible = !hidden.includes(entry.id);
            return `
              <div class="mobile-filter-row${entry.synthetic ? " mobile-filter-row-synthetic" : ""}">
                <span class="mobile-filter-name">${entry.label}</span>
                <button class="mobile-filter-toggle ${isVisible ? "active" : ""}"
                        data-mobile-action="toggle-show-tag"
                        data-tag-id="${escapeAttr(entry.id)}" aria-pressed="${isVisible}">${escapeHtml(t("common.show"))}</button>
                <button class="mobile-filter-toggle ${isHighlighted ? "active" : ""}"
                        data-mobile-action="toggle-highlight-tag"
                        data-tag-id="${escapeAttr(entry.id)}" aria-pressed="${isHighlighted}">${escapeHtml(t("app.sidebarTags.highlight"))}</button>
              </div>
            `;
          }).join("")}
        `).join("")}
        <label class="mobile-field mobile-dim-field">
          <span>${escapeHtml(t("mobile.filter.dimStrength"))}</span>
          <input type="range" min="0" max="95" step="5"
                 value="${Number.isFinite(appState.tagDimLevel) ? appState.tagDimLevel : 80}"
                 data-mobile-action="set-tag-dim"
                 aria-label="${escapeAttr(t("mobile.filter.dimStrength"))}" />
        </label>
      </div>
    </section>
  `;
}

export function renderUserInfoSheet({ callbacks, uiState }) {
  if (!uiState.userInfoOpen) return "";
  const user = callbacks.getCurrentUser?.();
  if (!user) return "";
  const username = user.username || "";
  const email = user.email || "";
  const initial = (username || email || "U").slice(0, 1).toUpperCase();
  // Username and email may be the same string (older accounts) — show only
  // one row in that case to avoid the duplicated line looking like a bug.
  const showEmailLine = email && email !== username;

  return `
    <div class="mobile-panel-backdrop" data-mobile-action="close-user-info"></div>
    <section role="dialog" aria-modal="true" class="mobile-panel mobile-user-info-panel">
      <header class="mobile-panel-header">
        <button class="mobile-icon-btn" data-mobile-action="close-user-info" aria-label="${escapeAttr(t("common.close"))}">${closeIcon()}</button>
        <div class="mobile-panel-title">${escapeHtml(t("html.profile.account"))}</div>
      </header>
      <div class="mobile-panel-body">
        <div class="mobile-user-info-card">
          <span class="mobile-user-avatar" aria-hidden="true">${escapeHtml(initial)}</span>
          <div class="mobile-user-info-text">
            <span class="mobile-user-info-name">${escapeHtml(username || email || t("mobile.account.user"))}</span>
            ${showEmailLine ? `<span class="mobile-user-info-email">${escapeHtml(email)}</span>` : ""}
            <span class="mobile-user-info-status">${escapeHtml(t("mobile.account.status"))}</span>
          </div>
        </div>
        <div class="mobile-section-title">${escapeHtml(t("mobile.appearance"))}</div>
        <div class="mobile-segmented mobile-theme-segmented">
          ${["light", "dark", "nord", "sepia"].map((theme) => `
            <button class="${(callbacks.getUITheme?.() || "dark") === theme ? "active" : ""}"
                    data-mobile-action="set-ui-theme"
                    data-theme="${theme}">${escapeHtml(t(THEME_KEYS[theme]))}</button>
          `).join("")}
        </div>
        <div class="mobile-section-title">${escapeHtml(t("lang.label"))}</div>
        <div class="mobile-segmented mobile-lang-segmented">
          ${LOCALES.map(({ code, name }) => `
            <button class="${getLocale() === code ? "active" : ""}"
                    data-mobile-action="set-locale"
                    data-locale="${code}" lang="${code}">${escapeHtml(name)}</button>
          `).join("")}
        </div>
        ${uiState.canInstall ? `
          <button class="mobile-doc-row mobile-action-doc-row" data-mobile-action="install-pwa">
            <span class="mobile-doc-main">
              <span class="mobile-doc-title">${escapeHtml(t("mobile.account.install"))}</span>
              <span class="mobile-doc-meta">${escapeHtml(t("mobile.account.installHint"))}</span>
            </span>
          </button>
        ` : ""}
        <div class="mobile-section-title">${escapeHtml(t("html.profile.security"))}</div>
        <button class="mobile-doc-row mobile-action-doc-row" data-mobile-action="regenerate-recovery">
          <span class="mobile-doc-main">
            <span class="mobile-doc-title">${escapeHtml(t("mobile.account.recoveryPhrase"))}</span>
            <span class="mobile-doc-meta">${escapeHtml(t("mobile.account.recoveryHint"))}</span>
          </span>
        </button>
        <button class="mobile-doc-row mobile-action-doc-row" data-mobile-action="mobile-logout-everywhere">
          <span class="mobile-doc-main">
            <span class="mobile-doc-title">${escapeHtml(t("html.profile.signOutEverywhere"))}</span>
            <span class="mobile-doc-meta">${escapeHtml(t("html.profile.signOutEverywhereHint"))}</span>
          </span>
        </button>
        <div class="mobile-note-actions">
          <button class="mobile-secondary-btn danger" data-mobile-action="mobile-logout">${escapeHtml(t("common.logout"))}</button>
          <button class="mobile-secondary-btn danger" data-mobile-action="delete-account">${escapeHtml(t("mobile.account.delete"))}</button>
        </div>
      </div>
    </section>
  `;
}

/**
 * Document properties, opened from the document name in the top bar.
 *
 * The name is the natural target for "things about this document" — stats,
 * access, tag filters. These used to live in the "More" tab next to account
 * settings and log out, which put document scope and account scope behind the
 * same button.
 */
export function renderDocPropertiesSheet(appState, deps) {
  const { callbacks, uiState } = deps;
  if (!uiState.docPropsOpen) return "";

  const nodeCount = callbacks.Store.getAll?.().length || 0;
  const noteCount = callbacks.sharedCallbacks.getDocNotes?.().length || 0;
  const readOnly = callbacks.Store.isReadOnly?.();
  const filterCount =
    (appState.highlightedTagIds?.length || 0) +
    (appState.hiddenTagIds?.length || 0);
  const docTitle = deps.getCurrentDocTitle?.(appState) || appState.data?.name || t("mobile.docProps.document");

  return `
    <div class="mobile-panel-backdrop" data-mobile-action="close-doc-props"></div>
    <section role="dialog" aria-modal="true" class="mobile-panel mobile-doc-props-panel">
      <header class="mobile-panel-header">
        <button class="mobile-icon-btn" data-mobile-action="close-doc-props" aria-label="${escapeAttr(t("common.close"))}">${closeIcon()}</button>
        <div class="mobile-panel-title">${escapeHtml(docTitle)}</div>
      </header>
      <div class="mobile-panel-body">
        <div class="mobile-stat-grid">
          <div class="mobile-stat"><span>${nodeCount}</span><label>${escapeHtml(t("mobile.nodes"))}</label></div>
          <div class="mobile-stat"><span>${noteCount}</span><label>${escapeHtml(t("mobile.docProps.docNotes"))}</label></div>
        </div>
        <div class="mobile-doc-size" data-storage-doc-id="${escapeAttr(appState.currentDocId)}" data-size-detail>${escapeHtml(t("vault.size.loading"))}</div>
        <div class="mobile-section-title">${escapeHtml(t("mobile.docProps.thisDocument"))}</div>
        <div class="mobile-more-list">
          <button class="mobile-doc-row mobile-action-doc-row" data-mobile-action="open-filter">
            <span class="mobile-doc-main">
              <span class="mobile-doc-title">${escapeHtml(t("mobile.docProps.tagFilters"))}</span>
              <span class="mobile-doc-meta">${escapeHtml(filterCount ? t("mobile.docProps.activeFilters", { count: filterCount }) : t("mobile.docProps.noneActive"))}</span>
            </span>
            <span class="mobile-chevron">${chevronIcon()}</span>
          </button>
          ${appState.currentDocId && engine.account.isSignedIn() && engine.map.storage() === "online" ? `<button class="mobile-doc-row mobile-action-doc-row" data-mobile-action="open-history" data-doc-id="${escapeAttr(appState.currentDocId)}">
            <span class="mobile-doc-main">
              <span class="mobile-doc-title">${escapeHtml(t("history.menu"))}</span>
              <span class="mobile-doc-meta">${escapeHtml(t("history.sectionHint"))}</span>
            </span>
            <span class="mobile-chevron">${chevronIcon()}</span>
          </button>` : ""}
          <div class="mobile-doc-row">
            <span class="mobile-doc-main">
              <span class="mobile-doc-title">${escapeHtml(t("mobile.docProps.access"))}</span>
              <span class="mobile-doc-meta">${escapeHtml(!readOnly ? t("mobile.docProps.editable") : callbacks.Store.isDeleteBlocked?.() ? t("mobile.mode.viewOnly") : t("mobile.mode.mapFull"))}</span>
            </span>
          </div>
        </div>
        ${renderDocSharingSection(uiState)}
      </div>
    </section>
  `;
}

/**
 * Sharing block inside the document properties sheet. Data is fetched
 * async when the sheet opens (uiState.docShareInfo, set by
 * loadDocShareInfo). Full management, mirroring the desktop share modal:
 * invite (owner/admin), per-member role change (per the role matrix),
 * remove member / stop sharing (owner — atomic rotate+revoke, recipients
 * get a recovery snapshot), cancel pending invitations. Client gating is
 * UX only; the server enforces roles authoritatively.
 */
const ROLES = ["owner", "admin", "editor", "viewer"];

/** "Owner", "Editor"… for a role (share area texts); null for an unknown one. */
function roleLabelOf(role) {
  return ROLES.includes(role) ? t(`share.role.${role}`) : null;
}

function assignableRolesFor(myRole, targetRole) {
  if (targetRole === "owner") return [];
  if (myRole === "owner") return ["viewer", "editor", "admin"];
  // Admins and editors may move non-admin users between viewer/editor.
  if ((myRole === "admin" || myRole === "editor") && targetRole !== "admin") {
    return ["viewer", "editor"];
  }
  return [];
}

function renderRoleSelect(userId, currentRole, roles, disabled) {
  return `
    <select class="mobile-role-select"
            data-mobile-action="set-member-role"
            data-user-id="${escapeAttr(userId)}"
            aria-label="${escapeAttr(t("mobile.share.role"))}"
            ${disabled ? "disabled" : ""}>
      ${roles.map((r) => `<option value="${r}" ${r === currentRole ? "selected" : ""}>${escapeHtml(roleLabelOf(r))}</option>`).join("")}
      ${roles.includes(currentRole) ? "" : `<option value="${escapeAttr(currentRole)}" selected>${escapeHtml(roleLabelOf(currentRole) || currentRole)}</option>`}
    </select>`;
}

// Member re-registered their encryption key: the owner restores their access.
function renderRestoreAccess(needed, userId, username, isOwner, busy) {
  if (!needed) return "";
  if (!isOwner) return `<span class="mobile-doc-meta">${escapeHtml(t("share.key.changed"))}</span>`;
  return `<button class="mobile-secondary-btn compact"
                  data-mobile-action="share-restore-access"
                  data-user-id="${escapeAttr(userId || "")}"
                  data-username="${escapeAttr(username || "")}"
                  ${busy ? "disabled" : ""}
                  title="${escapeAttr(t("share.key.reregistered", { name: username || t("mobile.share.thisUser") }))}">${escapeHtml(t("mobile.restore"))}</button>`;
}

// "Anyone with the link" (docs/plans/2026-10-28-link-sharing.md, "Redesign":
// the link is the access): the owner makes it, copies it, sets the role it
// gives, makes a new one or turns it off; an admin copies it; others see
// whether it is on.
function renderLinkBlock(info, myRole, busy) {
  const link = info.link;
  const isOwner = myRole === "owner";
  const manager = isOwner || myRole === "admin";
  if (!link && !isOwner) return "";
  const roleSelect = (attrs, role) => `
    <select class="mobile-role-select" ${attrs} aria-label="${escapeAttr(t("share.link.opensAs"))}" ${busy ? "disabled" : ""}>
      ${["viewer", "editor"].map((r) => `<option value="${r}" ${r === role ? "selected" : ""}>${escapeHtml(roleLabelOf(r))}</option>`).join("")}
    </select>`;
  const heading = (meta) => `
    <span class="mobile-doc-main">
      <span class="mobile-doc-title">${escapeHtml(t("share.link.heading"))}</span>
      <span class="mobile-doc-meta">${escapeHtml(meta)}</span>
    </span>`;
  if (!manager) {
    return `<div class="mobile-doc-row">${heading(t("share.link.status", { role: t(`share.roleInText.${link.role}`) }))}</div>`;
  }
  if (!link) {
    return `
      <div class="mobile-doc-row mobile-share-link-row">${heading(`${t("share.link.off")} · ${t("share.link.hintOff")}`)}</div>
      <div class="mobile-share-invite-row">
        ${roleSelect('id="mobile-share-link-role"', "viewer")}
        <button class="mobile-primary-btn compact" data-mobile-action="share-link-create" ${busy ? "disabled" : ""}>${escapeHtml(t("share.link.create"))}</button>
      </div>`;
  }
  const address = info.linkAddress || "";
  return `
    <div class="mobile-doc-row mobile-share-link-row">
      ${heading(`${t("share.link.on")} · ${t(isOwner ? "share.link.hintOn" : "share.link.ownerOnly")}`)}
      ${isOwner ? roleSelect('data-mobile-action="set-link-role"', link.role) : `<span class="mobile-doc-meta">${escapeHtml(roleLabelOf(link.role))}</span>`}
    </div>
    <div class="mobile-share-invite-row">
      <input class="mobile-input-control" readonly value="${escapeAttr(address)}" placeholder="${escapeAttr(t("share.link.preparing"))}" aria-label="${escapeAttr(t("share.link.heading"))}" />
      <button class="mobile-primary-btn compact" data-mobile-action="share-link-copy" ${address && !busy ? "" : "disabled"}>${escapeHtml(t("share.link.copy"))}</button>
    </div>
    ${isOwner ? `<div class="mobile-share-link-actions">
      <button class="mobile-secondary-btn compact" data-mobile-action="share-link-renew" ${busy ? "disabled" : ""}>${escapeHtml(t("share.link.renew"))}</button>
      <button class="mobile-secondary-btn compact" data-mobile-action="share-link-off" ${busy ? "disabled" : ""}>${escapeHtml(t("share.link.turnOff"))}</button>
    </div>` : ""}`;
}

// A map open by its link, not as a member: what that means, the link to
// share further, "Remove from my maps" (kept ones), or that the link no
// longer works.
function renderLinkHolder(held, busy) {
  const text = held.gone ? t(held.kept ? "share.link.gone.text" : "share.link.gone.textPage") : t("share.link.holder.text", { role: t(`share.roleInText.${held.role}`) });
  return `
    <div class="mobile-doc-row">
      <span class="mobile-doc-main">
        <span class="mobile-doc-title">${escapeHtml(t(held.gone ? "share.link.gone.title" : "share.link.holder.title"))}</span>
        <span class="mobile-doc-meta">${escapeHtml(text)}</span>
      </span>
    </div>
    ${held.gone ? "" : `<div class="mobile-share-invite-row">
      <input class="mobile-input-control" readonly value="${escapeAttr(held.address)}" aria-label="${escapeAttr(t("share.link.heading"))}" />
      <button class="mobile-primary-btn compact" data-mobile-action="share-link-copy" ${busy ? "disabled" : ""}>${escapeHtml(t("share.link.copy"))}</button>
    </div>`}
    ${held.kept ? `<button class="mobile-doc-row mobile-action-doc-row mobile-doc-row-danger" data-mobile-action="share-link-remove-held" ${busy ? "disabled" : ""}>
      <span class="mobile-doc-main"><span class="mobile-doc-title">${escapeHtml(t("share.link.holder.remove"))}</span></span>
    </button>` : ""}`;
}

function renderDocSharingSection(uiState) {
  const info = uiState.docShareInfo;
  const busy = !!uiState.shareBusy;
  const roleLabel = (r) => roleLabelOf(r) || r || "?";
  let body;
  if (!info || info.loading) {
    body = `<div class="mobile-doc-row"><span class="mobile-doc-main">
      <span class="mobile-doc-meta">${escapeHtml(t("mobile.share.loading"))}</span></span></div>`;
  } else if (info.held) {
    body = renderLinkHolder(info.held, busy);
  } else if (info.none || !info.share) {
    body = `<div class="mobile-doc-row"><span class="mobile-doc-main">
      <span class="mobile-doc-title">${escapeHtml(t("mobile.share.private"))}</span>
      <span class="mobile-doc-meta">${escapeHtml(t("mobile.share.notShared"))}</span></span></div>`;
  } else {
    const share = info.share;
    const myRole = share.role || "viewer";
    const canInvite = myRole === "owner" || myRole === "admin";
    const canRevoke = myRole === "owner";
    const members = (share.permissions || []).filter((x) => x.role !== "owner");
    const rows = [];

    if (share.owner_username) {
      rows.push(`
        <div class="mobile-doc-row">
          <span class="mobile-doc-main">
            <span class="mobile-doc-title">${escapeHtml(share.owner_username)}</span>
            <span class="mobile-doc-meta">${escapeHtml(myRole === "owner" ? t("mobile.share.ownerYou") : t("share.role.owner"))}</span>
          </span>
        </div>`);
    }

    for (const p of members) {
      const roles = assignableRolesFor(myRole, p.role);
      rows.push(`
        <div class="mobile-doc-row mobile-share-member-row">
          <span class="mobile-doc-main">
            <span class="mobile-doc-title">${escapeHtml(p.username || t("share.member.unknown"))}</span>
            ${roles.length ? "" : `<span class="mobile-doc-meta">${escapeHtml(roleLabel(p.role))}</span>`}
          </span>
          ${roles.length ? renderRoleSelect(p.user_id, p.role, roles, busy) : ""}
          ${renderRestoreAccess(p.key_restore_required, p.user_id, p.username, canRevoke, busy)}
          ${canRevoke ? `
            <button class="mobile-row-action"
                    data-mobile-action="share-remove-member"
                    data-user-id="${escapeAttr(p.user_id)}"
                    data-username="${escapeAttr(p.username || "")}"
                    data-link-on="${info.link ? "true" : "false"}"
                    ${busy ? "disabled" : ""}
                    aria-label="${escapeAttr(t("mobile.share.removeAria", { name: p.username || t("mobile.shell.user") }))}"
                    title="${escapeAttr(t("share.member.remove"))}">${trashIcon()}</button>
          ` : ""}
        </div>`);
    }

    for (const inv of share.pendingInvitations || []) {
      rows.push(`
        <div class="mobile-doc-row mobile-share-member-row">
          <span class="mobile-doc-main">
            <span class="mobile-doc-title">${escapeHtml(inv.invitee_username || t("share.member.unknown"))}</span>
            <span class="mobile-doc-meta">${escapeHtml(t("mobile.share.invitedPending", { role: roleLabel(inv.role) }))}</span>
          </span>
          ${renderRestoreAccess(inv.key_restore_required, inv.invitee_id, inv.invitee_username, canRevoke, busy)}
          ${canInvite ? `
            <button class="mobile-row-action"
                    data-mobile-action="share-cancel-invitation"
                    data-invitation-id="${escapeAttr(inv.id || "")}"
                    ${busy ? "disabled" : ""}
                    aria-label="${escapeAttr(t("share.pending.cancel"))}"
                    title="${escapeAttr(t("share.pending.cancel"))}">${closeIcon()}</button>
          ` : ""}
        </div>`);
    }

    if (myRole !== "owner") {
      rows.push(`<div class="mobile-doc-row"><span class="mobile-doc-main">
        <span class="mobile-doc-meta">${escapeHtml(t("mobile.share.yourRole", { role: roleLabel(myRole) }))}</span></span></div>`);
    }

    if (canInvite) {
      const draft = uiState.shareInviteDraft || {};
      rows.push(`
        <div class="mobile-share-invite-row">
          <input id="mobile-share-invite-name" class="mobile-input-control"
                 data-mobile-action="share-invite-input" data-field="name"
                 placeholder="${escapeAttr(t("mobile.share.invitePlaceholder"))}"
                 value="${escapeAttr(draft.name || "")}" ${busy ? "disabled" : ""} />
          <select id="mobile-share-invite-role" class="mobile-role-select"
                  data-mobile-action="share-invite-input" data-field="role"
                  aria-label="${escapeAttr(t("mobile.share.inviteRole"))}" ${busy ? "disabled" : ""}>
            ${["viewer", "editor", "admin"].map((r) => `<option value="${r}" ${(draft.role || "viewer") === r ? "selected" : ""}>${escapeHtml(roleLabelOf(r))}</option>`).join("")}
          </select>
          <button class="mobile-primary-btn compact"
                  data-mobile-action="share-invite-submit"
                  ${busy ? "disabled" : ""}>${escapeHtml(t("mobile.share.invite"))}</button>
        </div>`);
    }

    rows.push(renderLinkBlock(info, myRole, busy));

    if (canRevoke && (members.length > 0 || info.link)) {
      rows.push(`
        <button class="mobile-doc-row mobile-action-doc-row mobile-doc-row-danger"
                data-mobile-action="share-stop-sharing" ${busy ? "disabled" : ""}>
          <span class="mobile-doc-main">
            <span class="mobile-doc-title">${escapeHtml(t("vault.map.stopSharing"))}</span>
            <span class="mobile-doc-meta">${escapeHtml(t("mobile.share.stopHint"))}</span>
          </span>
        </button>`);
    }

    if (busy) {
      rows.push(`<div class="mobile-doc-row"><span class="mobile-doc-main">
        <span class="mobile-doc-meta">${escapeHtml(t("vault.dialog.working"))}</span></span></div>`);
    }
    body = rows.join("");
  }
  return `
    <div class="mobile-section-title">${escapeHtml(t("vault.map.sharing"))}</div>
    <div class="mobile-more-list">${body}</div>
  `;
}

/**
 * Notifications sheet — opened from the bell in the top bar. Today the
 * pending share invitations ARE the notification inbox (other server
 * notification types resolve into toasts/flows immediately).
 */
export function renderNotificationsSheet({ callbacks, uiState }) {
  if (!uiState.notificationsOpen) return "";
  let invitations = [];
  let pending = [];
  try {
    invitations = callbacks.getInvitations?.() || [];
    pending = callbacks.getPendingNotifications?.() || [];
  } catch { /* render empty state */ }

  return `
    <div class="mobile-panel-backdrop" data-mobile-action="close-notifications"></div>
    <section role="dialog" aria-modal="true" class="mobile-panel mobile-notifications-panel">
      <header class="mobile-panel-header">
        <button class="mobile-icon-btn" data-mobile-action="close-notifications" aria-label="${escapeAttr(t("common.close"))}">${closeIcon()}</button>
        <div class="mobile-panel-title">${escapeHtml(t("html.header.notifications"))}</div>
      </header>
      <div class="mobile-panel-body">
        ${invitations.length ? `
          <div class="mobile-section-title">${escapeHtml(t("mobile.invite.shareInvitations"))}</div>
          <div class="mobile-doc-list">
            ${invitations.map((inv) => {
              const title = inv.share_title || t("mobile.invite.untitled");
              const from = inv.inviter_username || t("mobile.invite.someone");
              const role = inv.role || "viewer";
              const id = escapeAttr(inv.id || "");
              return `
                <div class="mobile-doc-row mobile-invite-row">
                  <span class="mobile-doc-main">
                    <span class="mobile-doc-title">${escapeHtml(title)}</span>
                    <span class="mobile-doc-meta">${escapeHtml(invitationMeta(from, role))}</span>
                  </span>
                  <span class="mobile-invite-actions">
                    <button class="mobile-secondary-btn compact"
                            data-mobile-action="ignore-inviter"
                            data-invitation-id="${id}"
                            data-inviter="${escapeAttr(from)}">${escapeHtml(t("share.inbox.ignore"))}</button>
                    <button class="mobile-secondary-btn compact"
                            data-mobile-action="decline-invitation"
                            data-invitation-id="${id}">${escapeHtml(t("share.inbox.decline"))}</button>
                    <button class="mobile-primary-btn compact"
                            data-mobile-action="accept-invitation"
                            data-invitation-id="${id}">${escapeHtml(t("share.inbox.accept"))}</button>
                  </span>
                </div>
              `;
            }).join("")}
          </div>
        ` : ""}
        ${pending.length ? `
          <div class="mobile-section-title">${escapeHtml(t("html.header.notifications"))}</div>
          <div class="mobile-doc-list">
            ${pending.map((n) => renderGenericNotification(n, uiState)).join("")}
          </div>
        ` : ""}
        ${!invitations.length && !pending.length ? renderEmpty(t("mobile.notifications.empty")) : ""}
      </div>
    </section>
  `;
}

function renderGenericNotification(n, uiState) {
  const id = escapeAttr(n.id || "");
  // Stored notifications keep the REST field name (notification_type);
  // live WS payloads use `type` — accept both, like the desktop modal.
  const type = n.notification_type || n.type;
  const isRevoked = type === "share_revoked";
  // Someone asked for access to one of this account's maps: offer to grant
  // it (opens that map's sharing with their name filled in).
  const grantable = type === "access_request" && n.metadata?.doc_id && n.metadata?.requester_username;
  const recovering = uiState.shareRecovering
    && (uiState.shareRecovering === n.id || uiState.shareRecovering === n.metadata?.share_id);
  const title = n.title || (isRevoked ? t("app.revoked.title") : t("mobile.notifications.generic"));
  return `
    <div class="mobile-doc-row mobile-invite-row">
      <span class="mobile-doc-main">
        <span class="mobile-doc-title">${escapeHtml(title)}</span>
        ${n.message ? `<span class="mobile-doc-meta">${escapeHtml(n.message)}</span>` : ""}
      </span>
      <span class="mobile-invite-actions">
        <button class="mobile-secondary-btn compact"
                data-mobile-action="dismiss-notification"
                data-notification-id="${id}"
                ${recovering ? "disabled" : ""}>${escapeHtml(t("share.inbox.dismiss"))}</button>
        ${grantable ? `
          <button class="mobile-primary-btn compact"
                  data-mobile-action="grant-access-request"
                  data-notification-id="${id}"
                  data-doc-id="${escapeAttr(n.metadata.doc_id)}"
                  data-requester-username="${escapeAttr(n.metadata.requester_username)}"
                  title="${escapeAttr(t("share.inbox.grantTitle"))}">${escapeHtml(t("share.inbox.grant"))}</button>
        ` : ""}
        ${isRevoked ? `
          <button class="mobile-primary-btn compact"
                  data-mobile-action="recover-share"
                  data-notification-id="${id}"
                  data-share-id="${escapeAttr(n.metadata?.share_id || "")}"
                  data-share-title="${escapeAttr(n.metadata?.share_title || "")}"
                  ${recovering ? "disabled" : ""}>${escapeHtml(recovering ? t("mobile.notifications.recovering") : t("share.inbox.recover"))}</button>
        ` : ""}
      </span>
    </div>
  `;
}
